import { useCallback, useEffect, useRef, useState } from 'react';

const MENUITEM_SELECTOR = [
    'a[href][role="menuitem"]',
    'button[role="menuitem"]:not([disabled])',
    'input[role="menuitem"]:not([disabled])',
].join(', ');

const UP = 'ArrowUp';
const DOWN = 'ArrowDown';
const ESC = 'Escape';

// most of the DOM methods used in this hook are part of the HTMLElement
// interface, but the methods/events that have Node-like values are not
// guaranteed to implement HTMLElement so runtime checks are necessary. e.g.
// querySelectorAll returns Element[] therefore requires further typechecking
function isHTMLElement(node: null | Element | Node | EventTarget): node is HTMLElement {
    return !!(node && node instanceof HTMLElement);
}

// focus the first menuitem in the menu
function focusFirst<T extends HTMLElement | null>(menu: T): void {
    if (menu) {
        const firstMenuItem = menu.querySelector(MENUITEM_SELECTOR);
        if (isHTMLElement(firstMenuItem)) {
            setTimeout(() => {
                firstMenuItem.tabIndex = -1;
                firstMenuItem.focus();
            });
        }
    }
}

// remove tabIndex so that tabbing an open menu moves the activeElement out of the menu
function removeTabIndex<T extends HTMLElement | null>(menu: T): void {
    if (menu) {
        const menuItems = Array.from(menu.querySelectorAll(MENUITEM_SELECTOR));
        if (menuItems.length === 0) {
            console.warn(
                `dibs-elements | Menu | Menu requires children that are 'a', 'button', or 'input' and have 'role="menuitem"'`
            );
        }
        menuItems.forEach(node => {
            if (isHTMLElement(node)) {
                node.tabIndex = -1;
            }
        });
    }
}

function changeFocus(menu: HTMLElement, step: number): void {
    const current = document.activeElement;

    // if focus is not on a menuitem, focus first menuitem
    if (!menu.contains(current)) {
        focusFirst(menu);
        return;
    }

    // create array of menuitems. use Array.from for IE11 compatibility
    const menuItems = Array.from(menu.querySelectorAll(MENUITEM_SELECTOR));

    // get index of current menuitem
    const i = menuItems.findIndex(node => node.isSameNode(current));

    // if current menuitem is not found, fallback to focusing first item
    if (i === -1) {
        focusFirst(menu);
        return;
    }

    const l = menuItems.length;

    // use modulo to traverse array circularly. bc modulo can be negative in JS, it requires
    // addition to ensure the result is always range 0 through l-1, not -(l-1) through l-1
    const next = menuItems[(((i + step) % l) + l) % l];
    if (isHTMLElement(next)) {
        next.focus();
        next.tabIndex = 0;
        if (isHTMLElement(current)) {
            current.tabIndex = -1;
        }
    }
}

// be sure to wrap onClose in useCallback if the onClose is declared in a function component body
// do not declare onClose as an inline function in the hook call
export function useMenu<T extends HTMLElement | null>(
    isOpen: boolean,
    onClose: () => void,
    closeOnBlur: boolean | undefined = true
): (node: T) => void {
    const [menu, setMenu] = useState<HTMLElement | null>(null);
    const returnFocusRef = useRef<Element | null>(null);

    // closing menu with [Escape] returns focus to the menu trigger
    const returnFocusToTrigger = useCallback(
        function(): void {
            if (isHTMLElement(returnFocusRef.current)) {
                returnFocusRef.current.focus();
            }
            onClose();
        },
        [onClose]
    );

    // handle key presses for menu controls
    const handleKeyDown = useCallback(
        function(this: HTMLElement, e: KeyboardEvent): void {
            switch (e.key) {
                case UP:
                    e.preventDefault();
                    changeFocus(this, -1);
                    break;
                case DOWN:
                    e.preventDefault();
                    changeFocus(this, 1);
                    break;
                case ESC:
                    e.preventDefault();
                    onClose();
                    returnFocusToTrigger();
                    break;
            }
        },
        [returnFocusToTrigger, onClose]
    );

    // close menu on focus out where relatedTarget is not inside menu or is not
    // available (click outside of iframe, on browser controls, etc.)
    const handleFocusOut = useCallback(
        function(this: HTMLElement, e: FocusEvent): void {
            if (!isHTMLElement(e.relatedTarget) || !this.contains(e.relatedTarget)) {
                onClose();
            }
        },
        [onClose]
    );

    useEffect(() => {
        if (isOpen) {
            // store menu trigger to return focus in handleEscapeClose
            returnFocusRef.current = document.activeElement;
            removeTabIndex(menu);
            // focus first menuitem when menu is opened
            focusFirst(menu);
        }

        if (menu) {
            menu.hidden = !isOpen;
        }
    }, [menu, isOpen]);

    useEffect(() => {
        if (menu) {
            if (menu.getAttribute('role') !== 'menu') {
                menu.setAttribute('role', 'menu');
            }
            menu.addEventListener('keydown', handleKeyDown);
            if (closeOnBlur) {
                menu.addEventListener('focusout', handleFocusOut);
            }
        }
        return () => {
            if (menu) {
                menu.removeEventListener('focusout', handleFocusOut);
                menu.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, [menu, handleKeyDown, handleFocusOut, closeOnBlur]);

    // assign to consuming component's ref prop
    const ref = useCallback(<T extends HTMLElement | null>(node: T) => {
        if (node) {
            setMenu(node);
        }
    }, []);
    return ref;
}
